import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useCallback } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useFragment } from "react-relay";

import { SlotLockCountdownTimer } from "@/features/LockSlots/components/SlotLockCountdownTimer/SlotLockCountdownTimer";
import { Button } from "@whoosh/design-system/src/components/controls";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import {
  Box,
  HStack,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { useOpenConfirmedBookingModalAfterCompletedBooking } from "../../../BookingSummary/hooks/useOpenConfirmedBookingModalAfterCompletedBooking";
import { useCompleteDiningReservation } from "../../hooks/useCompleteDiningReservation";
import type { DiningReservationFooter_booking$key } from "./__generated__/DiningReservationFooter_booking.graphql";

type Props = {
  bookingRef: DiningReservationFooter_booking$key | null;
  handleGoBack: () => void;
};

export const DiningReservationFooter = ({
  bookingRef,
  handleGoBack,
}: Props) => {
  const booking = useFragment(DiningReservationFooterFragment, bookingRef);
  const insets = useSafeAreaInsets();
  const { showToast } = useToast();

  const bookingId = booking?.id ?? null;
  const isDraft = booking?.status === "DRAFT";
  const lockedUntil = booking?.diningReservationHold?.expiresAt ?? null;

  const [completeReservation, isCompletingReservation] =
    useCompleteDiningReservation();
  const handleOpenConfirmedBookingAfterCompletedBooking =
    useOpenConfirmedBookingModalAfterCompletedBooking();

  const handleTimeExpire = useCallback(() => {
    showToast({
      title: t`Sorry, but your timer has expired`,
      semanticStyle: "error",
      toastId: "dining-timer-expired",
    });
    handleGoBack();
  }, [handleGoBack, showToast]);

  const handleCompleteReservation = useCallback(() => {
    if (!bookingId) {
      return;
    }
    completeReservation({
      input: {
        bookingId,
      },
      onCompleted: handleOpenConfirmedBookingAfterCompletedBooking,
    });
  }, [
    bookingId,
    completeReservation,
    handleOpenConfirmedBookingAfterCompletedBooking,
  ]);

  if (!isDraft) {
    return null;
  }

  return (
    <Box
      padding={5}
      style={{ paddingBottom: Math.max(insets.bottom, 20) }}
      backgroundColor="surface-primary"
      borderTopWidth={1}
      borderTopColor="surface-dividers"
    >
      <VStack gap={5}>
        {lockedUntil ? (
          <HStack justifyContent="center">
            <SlotLockCountdownTimer
              lockedUntil={lockedUntil}
              onExpireCallback={handleTimeExpire}
            />
          </HStack>
        ) : null}
        <Button
          onPress={handleCompleteReservation}
          isLoading={isCompletingReservation}
          accessibilityLabel={t`Complete reservation`}
        >
          {t`Complete Reservation`}
        </Button>
      </VStack>
    </Box>
  );
};

export const DiningReservationFooterFragment = graphql`
  fragment DiningReservationFooter_booking on Booking {
    id
    ... on DiningBooking {
      status
      diningReservationHold {
        expiresAt
      }
    }
  }
`;
